import { Button, Popconfirm, Space, Table, Tag, notification } from "antd";
import {
  DeleteTwoTone,
  EditTwoTone,
  FilePdfTwoTone,
  FolderOpenOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import React, { useCallback, useMemo } from "react";
import { useEffect, useState } from "react";

import {
  deleteNoticeById,
  togglePublishedById,
  toggleArchivedById,
} from "../../store/api/notices";
import AddNoticePopup from "./AddNoticePopup";
import { config } from "../../config";
import { NOTICE_CATEGORY_LABELS } from "../../constants";
import NoticesToolbar from "./NoticesToolbar";

const NoticesTable = ({
  notices,
  handleFetch,
  loading,
  total,
  showFilters,
  visibleColumns,
  showDeleteAction = true,
  showArchiveAction = true,
  allowPublish = true,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const [filters, setFilters] = useState({
    category: "all",
    startDate: null,
    endDate: null,
    searchKeyword: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const refetchData = useCallback(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  useEffect(() => {
    handleFetch({
      ...filters,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [filters, pagination, handleFetch]);

  const handleFiltersChange = (filterKey, filterValue) => {
    setFilters((prev) => ({ ...prev, [filterKey]: filterValue }));
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
    });
  };

  const handleTableChange = (pagination) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleArchive = (item) => {
    setConfirmLoading(true);
    toggleArchivedById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const handleDelete = (item) => {
    setConfirmLoading(true);
    deleteNoticeById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({ message: data.message });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const changeStatus = (item) => {
    setConfirmLoading(true);
    togglePublishedById(item.id)
      .then(({ data }) => {
        setConfirmLoading(false);
        notification.success({
          message: item.isPublished
            ? "Notice unpublished succesfully"
            : "Notice published successfully",
        });
        refetchData();
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  const closePopup = () => {
    setIsModalVisible(false);
    setActiveItem(null);
  };

  const renderCategory = (category) => {
    const colors = {
      PRESS_RELEASE: "red",
      PROCUREMENT: "cyan",
      HR: "purple",
      OTHERS: "orange",
    };

    return (
      <Tag color={colors[category]}>{NOTICE_CATEGORY_LABELS[category]}</Tag>
    );
  };

  const allColumns = [
    {
      title: "Date",
      dataIndex: "noticeDate",
      render: (_, record) => {
        return (
          <p>
            {new Date(_).toLocaleDateString("en-GB", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </p>
        );
      },
      width: "150px",
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (_, record) => {
        return (
          <>
            <Space>{_ ? renderCategory(_) : "N/A"}</Space>
          </>
        );
      },
      width: "150px",
    },
    {
      title: "Notice",
      dataIndex: "filePath",
      render: (_, record) => {
        return (
          <FilePdfTwoTone
            onClick={() => {
              window.open(`${config.filesBaseUrl}/${_}`, "_blank");
            }}
            style={{
              fontSize: "30px",
              cursor: "pointer",
            }}
          />
        );
      },
      width: "100px",
    },
    // {
    //   title: "Published By",
    //   dataIndex: "publishedBy",
    //   render: (_, record) => {
    //     return <p> {_?.email}</p>;
    //   },
    // },
    {
      title: "Published on www",
      dataIndex: "isPublished",
      render: (_, record) => {
        return (
          <>
            <Space style={{ justifyContent: "space-between" }}>
              {_ ? (
                <Tag color="green" style={{ width: 40 }}>
                  Yes
                </Tag>
              ) : (
                <Tag color="red" style={{ width: 40 }}>
                  No
                </Tag>
              )}
              {allowPublish && (
                <Popconfirm
                  title={
                    record.isPublished
                      ? "Are you sure want to unpublish this notice?"
                      : "Are you sure want to publish this notice?"
                  }
                  onConfirm={() => changeStatus(record)}
                >
                  <Button style={{ width: "100px" }} type="default">
                    {_ ? "Unpublish" : "Publish"}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </>
        );
      },
      width: allowPublish ? "200px" : "100px",
    },

    {
      title: "Show on popup",
      dataIndex: "showOnPopup",
      render: (value, record) => {
        return (
          <>
            <Space>
              {value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}
            </Space>
          </>
        );
      },
      width: "100px",
    },
    {
      title: "Show on popup until",
      dataIndex: "showOnPopupUntil",
      render: (value, record) => {
        return (
          <p>
            {value
              ? new Date(value).toLocaleDateString("en-GB", {
                  weekday: "short",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "-"}
          </p>
        );
      },
      width: "150px",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: "100px",
      render: (_, record) => {
        return (
          <Space style={{ justifyContent: "center", width: "100%" }}>
            <EditTwoTone
              title="Edit"
              twoToneColor="#1890ff"
              style={{
                fontSize: "20px",
                cursor: "pointer",
              }}
              onClick={() => {
                setActiveItem(record);
                setIsModalVisible(true);
              }}
            />

            {showArchiveAction && (
              <Popconfirm
                confirmLoading={confirmLoading}
                title={
                  record.isArchived
                    ? "Are you sure you want to unarchive this notice?"
                    : "Are you sure want to archive this notice?"
                }
                onConfirm={() => handleArchive(record)}
                placement="topRight"
              >
                {record.isArchived ? (
                  <UndoOutlined
                    title="Unarchive"
                    style={{
                      color: "gray",
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <FolderOpenOutlined
                    title="Archive"
                    style={{
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "gray",
                    }}
                  />
                )}
              </Popconfirm>
            )}
            {showDeleteAction && (
              <Popconfirm
                confirmLoading={confirmLoading}
                title="Are you sure want to delete this notice?"
                onConfirm={() => handleDelete(record)}
                placement="topRight"
              >
                <DeleteTwoTone
                  title="Delete"
                  twoToneColor="#ff0000"
                  style={{
                    color: "gray",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const columns = useMemo(() => {
    if (!visibleColumns?.length) {
      return allColumns;
    }

    return allColumns.filter(({ dataIndex }) =>
      visibleColumns.includes(dataIndex)
    );
  });

  return (
    <>
      {isModalVisible && (
        <AddNoticePopup
          closePopup={closePopup}
          item={activeItem}
          onCreateSuccess={refetchData}
          onUpdateSuccess={refetchData}
        />
      )}
      <NoticesToolbar
        showFilters={showFilters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onAddNotice={() => setIsModalVisible(true)}
      />
      <Table
        dataSource={notices}
        columns={columns}
        loading={loading}
        bordered
        rowKey="id"
        pagination={{
          pageSizeOptions: ["10", "20", "50"],
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
        }}
        onChange={handleTableChange}
      />
    </>
  );
};

export default NoticesTable;
